import {
    AbsenceValue,
    CellSelection,
    detachCell,
    FREE_CELL,
    isAbsence,
    isWork,
    PlanCell,
    PlanColumn,
    PlanRow,
    sameWork,
    workCell,
} from "./plan-types";
import { readQueue, RowQueue, toQueueInsertIndex, writeQueue } from "./plan-queue";
import { pushRight, queueBlocks, renderQueue } from "./plan-layout";
import { PlanGrid, PlanSlot, slotsFrom, slotsOfRange } from "./plan-scale";
import { mongoObjectId } from "modules/common/services/strings";

/**
 * Базовые операции плана — свободное позиционирование.
 *
 * Блок занимает те дни, на которые его поставили, и никого не двигает: ось здесь
 * это календарь, и автоматический сдвиг менял бы сроки работ, которых пользователь
 * не касался. Накрытые дни соседа заменяются, уцелевшая часть остаётся на месте.
 * Каскадный сдвиг доступен отдельными командами — см. plan-shift.ts.
 *
 * Закреплённые дни (прошедшие и дни отсутствия) не меняются — работы их обтекают
 * так же, как обтекают невидимые выходные.
 *
 * Все функции иммутабельны и возвращают null, если операция невозможна.
 */

export interface MoveRange {
    fromRow: number;
    toRow: number;
    /** Колонка начала переносимого куска */
    start: number;
    /** Длина куска в колонках */
    length: number;
    /** Колонка, на которую бросили левый край */
    target: number;
}

/** Индексы очереди, попадающие в диапазон колонок */
export function queueIndexes(queue: RowQueue, start: number, length: number): number[] {
    const result: number[] = [];

    for (let i = 0; i < queue.columns.length; i++) {
        const column = queue.columns[i];

        if (column >= start && column < start + length) {
            result.push(i);
        }
    }

    return result;
}

/** Записать последовательность дней начиная с колонки, перезаписывая занятое */
function paint(queue: RowQueue, cells: PlanCell[], from: number, values: PlanCell[]): boolean {
    const at = toQueueInsertIndex(queue, from);

    if (at + values.length > cells.length) {
        return false;
    }

    values.forEach((value, index) => {
        cells[at + index] = value;
    });

    return true;
}

function clearAt(cells: PlanCell[], indexes: number[]) {
    indexes.forEach((index) => {
        cells[index] = FREE_CELL;
    });
}

/** Перенести дни на новое место: исходные освобождаются, целевые перезаписываются */
export function moveRange(rows: PlanRow[], columns: PlanColumn[], move: MoveRange): PlanRow[] | null {
    const { fromRow, toRow, start, length, target } = move;

    if (!rows[fromRow] || !rows[toRow] || length < 1) {
        return null;
    }

    const sourceQueue = readQueue(rows[fromRow], columns);
    const picked = queueIndexes(sourceQueue, start, length);

    if (!picked.length) {
        return null;
    }

    const values = picked.map((index) => sourceQueue.cells[index]);

    if (fromRow === toRow) {
        const cells = sourceQueue.cells.slice();

        clearAt(cells, picked);

        if (!paint(sourceQueue, cells, target, values)) {
            return null;
        }

        const result = rows.slice();
        result[fromRow] = writeQueue(rows[fromRow], sourceQueue, cells);

        return result;
    }

    const targetQueue = readQueue(rows[toRow], columns);
    const targetCells = targetQueue.cells.slice();

    if (!paint(targetQueue, targetCells, target, values)) {
        return null;
    }

    const sourceCells = sourceQueue.cells.slice();
    clearAt(sourceCells, picked);

    const result = rows.slice();
    result[fromRow] = writeQueue(rows[fromRow], sourceQueue, sourceCells);
    result[toRow] = writeQueue(rows[toRow], targetQueue, targetCells);

    return result;
}

/** Скопировать дни на новое место, не трогая исходные */
export function copyRange(rows: PlanRow[], columns: PlanColumn[], move: MoveRange): PlanRow[] | null {
    const { fromRow, toRow, start, length, target } = move;

    if (!rows[fromRow] || !rows[toRow] || length < 1) {
        return null;
    }

    // копия — не та же работа плана: бэк подберёт её сам
    const values = readRange(rows, columns, fromRow, start, length).map(detachCell);

    if (!values.length) {
        return null;
    }

    return pasteRange(rows, columns, toRow, target, values);
}

/**
 * Правый край блока.
 *
 * По свободным дням блок расходится, никого не трогая. Как только упирается
 * в соседа — толкает его вправо, а с ним и всю цепочку идущих следом работ.
 * Блок, начавшийся в прошлом, тоже можно продлить: прошлые дни не меняются,
 * дописываются только те, что с текущей даты и дальше.
 */
export function resizeBlockRight(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    blockStart: number,
    blockLength: number,
    newLength: number
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || newLength < 1 || newLength === blockLength) {
        return null;
    }

    const source = row.cells[blockStart];

    if (!isWork(source)) {
        return null;
    }

    const queue = readQueue(row, columns);
    const total = queue.cells.length;

    // очередь не содержит прошедших дней, поэтому для блока из прошлого
    // якорь начнётся с первого редактируемого дня — ровно то, что нужно
    const anchorStart = toQueueInsertIndex(queue, blockStart);
    const anchorEnd = toQueueInsertIndex(queue, blockStart + newLength);

    if (anchorEnd - anchorStart < 1) {
        return null;
    }

    const cells = queue.cells.slice();
    clearAt(cells, queueIndexes(queue, blockStart, blockLength));

    const placed = pushRight(total, { start: anchorStart, end: anchorEnd }, queueBlocks(cells));

    if (!placed) {
        return null;
    }

    const values: PlanCell[] = [];

    for (let i = anchorStart; i < anchorEnd; i++) {
        values.push(source);
    }

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, renderQueue(total, anchorStart, values, placed));

    return result;
}

/**
 * Левый край блока.
 *
 * По свободным дням тоже расходится свободно, но соседа слева не двигает,
 * а перезаписывает: двигать его было бы некуда — там прошлое.
 */
export function resizeBlockLeft(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    blockStart: number,
    blockLength: number,
    newStart: number
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || newStart === blockStart) {
        return null;
    }

    const source = row.cells[blockStart];

    if (!isWork(source)) {
        return null;
    }

    const queue = readQueue(row, columns);
    const cells = queue.cells.slice();

    // правый край остаётся на месте
    const anchorEnd = toQueueInsertIndex(queue, blockStart + blockLength);
    const anchorStart = toQueueInsertIndex(queue, newStart);

    if (anchorEnd - anchorStart < 1) {
        return null;
    }

    clearAt(cells, queueIndexes(queue, blockStart, blockLength));

    for (let i = anchorStart; i < anchorEnd; i++) {
        cells[i] = source;
    }

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/** Заполнить выделенные дни проектом (и разделом, если выбран) */
export function fillRange(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number,
    orderId: string,
    sectionId?: string | null
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return null;
    }

    const queue = readQueue(row, columns);
    const picked = queueIndexes(queue, start, length);

    if (!picked.length) {
        return null;
    }

    const cells = queue.cells.slice();

    const value = workCell(orderId, sectionId);

    picked.forEach((index) => {
        const current = cells[index];

        // тот же проект и раздел уже стоит — день остаётся на своей работе плана
        cells[index] = current.kind === "work" && sameWork(current, value) ? current : value;
    });

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/** Освободить выделенные дни на месте */
export function clearRange(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return null;
    }

    // дни отсутствия закреплены за датой и в очередь не входят — снимаем их напрямую.
    // Хвост работ влево при этом не подтягивается: каскад остаётся отдельной командой
    const target = releaseAbsence(row, columns, start, length);
    const removed = target !== row;

    const queue = readQueue(target, columns);
    const picked = queueIndexes(queue, start, length);

    if (!picked.length) {
        if (!removed) {
            return null;
        }

        const onlyAbsence = rows.slice();
        onlyAbsence[rowIndex] = target;

        return onlyAbsence;
    }

    const cells = queue.cells.slice();
    clearAt(cells, picked);

    const result = rows.slice();
    result[rowIndex] = writeQueue(target, queue, cells);

    return result;
}

/** Снять отсутствие с диапазона колонок, не трогая стоящие там работы */
export function releaseAbsence(row: PlanRow, columns: PlanColumn[], start: number, length: number): PlanRow {
    const cells = row.cells.slice();
    let changed = false;

    for (let index = start; index < start + length && index < columns.length; index++) {
        if (!columns[index].isPast && isAbsence(cells[index])) {
            cells[index] = FREE_CELL;
            changed = true;
        }
    }

    return changed ? { ...row, cells } : row;
}

/**
 * Перенести отсутствие на другие дни — в том числе другому сотруднику.
 *
 * Этим же путём идут растягивание за край (from и to — старые и новые границы
 * блока) и копирование с Ctrl (from = null: со старого места ничего не снимаем).
 * Снимаем сначала, ставим потом — иначе работы толкались бы ещё и от старых дней.
 */
export function moveAbsence(
    rows: PlanRow[],
    columns: PlanColumn[],
    absence: AbsenceValue,
    from: CellSelection | null,
    to: CellSelection
): PlanRow[] | null {
    if (!rows[to.rowIndex] || to.length < 1) {
        return null;
    }

    let next = rows;

    if (from) {
        if (!rows[from.rowIndex]) {
            return null;
        }

        const released = releaseAbsence(rows[from.rowIndex], columns, from.start, from.length);

        if (released !== rows[from.rowIndex]) {
            next = rows.slice();
            next[from.rowIndex] = released;
        }
    }

    return fillAbsence(next, columns, to.rowIndex, to.start, to.length, absence);
}

/**
 * Поставить день отсутствия — отпуск, больничный, без сохранения.
 *
 * Ведёт себя как расширение блока правым краем: по свободным дням отсутствие просто
 * встаёт на место и никого не трогает, а наехав на работы — толкает их вправо вместе
 * со всей цепочкой. Перезаписывать работы нельзя: сотрудник ушёл в отпуск, а работу
 * всё равно надо сделать — она обязана уехать, а не пропасть.
 */
export function fillAbsence(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number,
    absence: AbsenceValue
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return null;
    }

    const value: PlanCell = { kind: "absence", type: absence.type, shortName: absence.shortName };

    // дни, уже отмеченные отсутствием, в очередь не входят — их меняем прямо в колонках:
    // это смена типа на месте, работы она не задевает
    const retyped = row.cells.slice();
    let changed = false;

    for (let index = start; index < start + length && index < columns.length; index++) {
        if (!columns[index].isPast && isAbsence(retyped[index])) {
            retyped[index] = value;
            changed = true;
        }
    }

    const target: PlanRow = changed ? { ...row, cells: retyped } : row;

    const queue = readQueue(target, columns);
    const total = queue.cells.length;

    const anchorStart = toQueueInsertIndex(queue, start);
    const anchorEnd = toQueueInsertIndex(queue, start + length);

    if (anchorEnd - anchorStart < 1) {
        // незакреплённых дней в диапазоне не осталось: либо это прошлое, либо
        // всё уже было отсутствием — тогда мы только что сменили ему тип
        if (!changed) {
            return null;
        }

        const onlyRetyped = rows.slice();
        onlyRetyped[rowIndex] = target;

        return onlyRetyped;
    }

    // блоки берём как есть: накрытый блок уезжает вправо целиком, а не разрезается —
    // ровно так же ведёт себя расширение блока правым краем
    const placed = pushRight(total, { start: anchorStart, end: anchorEnd }, queueBlocks(queue.cells));

    if (!placed) {
        return null;
    }

    const values: PlanCell[] = [];

    for (let i = anchorStart; i < anchorEnd; i++) {
        values.push(value);
    }

    const result = rows.slice();
    result[rowIndex] = writeQueue(target, queue, renderQueue(total, anchorStart, values, placed));

    return result;
}

/**
 * Содержимое диапазона колонок — то, что уходит в буфер обмена.
 * Читает и прошедшие дни: менять их нельзя, а копировать из них можно.
 */
export function readRange(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number
): PlanCell[] {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return [];
    }

    const result: PlanCell[] = [];

    for (let index = start; index < start + length && index < columns.length; index++) {
        const cell = row.cells[index];

        // дни отсутствия привязаны к дате, копировать их бессмысленно
        if (cell && !isAbsence(cell)) {
            result.push(cell);
        }
    }

    return result;
}

/**
 * Есть ли в диапазоне что стирать: работы или дни отсутствия на редактируемых днях.
 * В отличие от `readRange`, отсутствия здесь считаются — очистить их можно, хотя
 * скопировать нельзя.
 */
export function hasContent(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number
): boolean {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return false;
    }

    for (let index = start; index < start + length && index < columns.length; index++) {
        const cell = row.cells[index];

        if (!columns[index].isPast && cell && (isWork(cell) || isAbsence(cell))) {
            return true;
        }
    }

    return false;
}

/** Вставить дни из буфера начиная с колонки, перезаписывая то, что там стоит */
export function pasteRange(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    at: number,
    payload: PlanCell[]
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || !payload.length) {
        return null;
    }

    const queue = readQueue(row, columns);
    const cells = queue.cells.slice();

    if (!paint(queue, cells, at, payload)) {
        return null;
    }

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/* ------------------------------------------------------------------ *
 * Операции недельного масштаба — единица переноса ячейка, а не день.
 *
 * Недели в сетке разной длины: последняя неделя месяца обрезается его границей,
 * в неделе с праздником рабочих дней меньше. Считать такой перенос в днях нельзя —
 * три скопированные ячейки встали бы на другое число ячеек и разъехались с сеткой.
 * Поэтому содержимое переносится ячейка-в-ячейку, а внутри ячейки подгоняется
 * по длине: лишние дни отбрасываются, недостающие повторяют последний день.
 * ------------------------------------------------------------------ */

/** Содержимое диапазона, разложенное по ячейкам сетки: одна группа — одна неделя */
export function readSlots(
    rows: PlanRow[],
    columns: PlanColumn[],
    grid: PlanGrid,
    rowIndex: number,
    start: number,
    length: number
): PlanCell[][] {
    const row = rows[rowIndex];

    if (!row || length < 1) {
        return [];
    }

    return slotsOfRange(grid, start, length).map((slot) => {
        const from = Math.max(slot.start, start);
        const to = Math.min(slot.start + slot.length, start + length);

        return readRange(rows, columns, rowIndex, from, to - from);
    });
}

/**
 * Записать группы в ячейки подряд, начиная с ячейки колонки `at`.
 *
 * Внутри ячейки день j получает значение `group[min(j, group.length - 1)]`:
 * если в целевой неделе дней меньше — хвост источника просто не переносится,
 * если больше — последний день источника растягивается на остаток недели.
 */
function paintSlots(queue: RowQueue, cells: PlanCell[], targets: PlanSlot[], payload: PlanCell[][]): void {
    targets.forEach((slot, index) => {
        const group = payload[index];

        if (!group || !group.length) {
            return;
        }

        queueIndexes(queue, slot.start, slot.length).forEach((queueIndex, day) => {
            cells[queueIndex] = group[Math.min(day, group.length - 1)];
        });
    });
}

/** Вставить ячейки из буфера начиная с недели, в которую попала колонка */
export function pasteSlots(
    rows: PlanRow[],
    columns: PlanColumn[],
    grid: PlanGrid,
    rowIndex: number,
    at: number,
    payload: PlanCell[][]
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || !payload.length) {
        return null;
    }

    const targets = slotsFrom(grid, at, payload.length);

    if (!targets) {
        return null;
    }

    const queue = readQueue(row, columns);
    const cells = queue.cells.slice();

    paintSlots(queue, cells, targets, payload);

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/** Перенести ячейки на новое место: исходные освобождаются, целевые перезаписываются */
export function moveSlots(rows: PlanRow[], columns: PlanColumn[], grid: PlanGrid, move: MoveRange): PlanRow[] | null {
    const { fromRow, toRow, start, length, target } = move;

    if (!rows[fromRow] || !rows[toRow] || length < 1) {
        return null;
    }

    const payload = readSlots(rows, columns, grid, fromRow, start, length);

    if (!payload.length) {
        return null;
    }

    const targets = slotsFrom(grid, target, payload.length);

    if (!targets) {
        return null;
    }

    const sourceQueue = readQueue(rows[fromRow], columns);
    const sourceCells = sourceQueue.cells.slice();

    clearAt(sourceCells, queueIndexes(sourceQueue, start, length));

    const result = rows.slice();

    // внутри одной строки очистка и запись идут по одной очереди — иначе
    // перенос на соседнюю неделю стёр бы то, что только что положили
    if (fromRow === toRow) {
        paintSlots(sourceQueue, sourceCells, targets, payload);
        result[fromRow] = writeQueue(rows[fromRow], sourceQueue, sourceCells);

        return result;
    }

    const targetQueue = readQueue(rows[toRow], columns);
    const targetCells = targetQueue.cells.slice();

    paintSlots(targetQueue, targetCells, targets, payload);

    result[fromRow] = writeQueue(rows[fromRow], sourceQueue, sourceCells);
    result[toRow] = writeQueue(rows[toRow], targetQueue, targetCells);

    return result;
}

/** Скопировать ячейки на новое место, не трогая исходные */
export function copySlots(rows: PlanRow[], columns: PlanColumn[], grid: PlanGrid, move: MoveRange): PlanRow[] | null {
    const { fromRow, toRow, start, length, target } = move;

    if (!rows[fromRow] || !rows[toRow] || length < 1) {
        return null;
    }

    const payload = readSlots(rows, columns, grid, fromRow, start, length).map((group) => group.map(detachCell));

    if (!payload.length) {
        return null;
    }

    return pasteSlots(rows, columns, grid, toRow, target, payload);
}

/* ------------------------------------------------------------------ *
 * Работы плана: разделить и объединить. Как и остальные правки, копятся на странице
 * и уходят на сервер по «Сохранить»: сервер сам заведёт работу с новым id и удалит
 * работу, у которой не осталось дней.
 * ------------------------------------------------------------------ */

/**
 * Новый id работы — в формате Mongo ObjectId: сервер сохранит работу под ним,
 * как если бы завёл её сам.
 */
export const newAssignmentId = mongoObjectId;

/**
 * «Разделить работу здесь»: дни работы начиная с колонки уходят в новую работу.
 * null — делить нечего: до колонки нет дней этой работы или колонка в прошлом.
 */
export function splitWork(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    column: number,
    newId: string
): PlanRow[] | null {
    const row = rows[rowIndex];
    const cell = row ? row.cells[column] : undefined;

    if (!row || !cell || !isWork(cell) || !cell.assignmentId || !columns[column] || columns[column].isPast) {
        return null;
    }

    const id = cell.assignmentId;
    const before = row.cells.slice(0, column).some((value) => isWork(value) && value.assignmentId === id);

    if (!before) {
        return null;
    }

    const cells = row.cells.map((value, index) =>
        index >= column && isWork(value) && value.assignmentId === id ? { ...value, assignmentId: newId } : value
    );

    const result = rows.slice();
    result[rowIndex] = { ...row, cells };

    return result;
}

/**
 * «Объединить с соседней»: дни работы otherId переходят в работу id. Прошедшие дни
 * не меняются, поэтому у вливаемой работы их быть не должно: страница вливает правую
 * (более позднюю) работу в левую.
 */
export function mergeWork(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    id: string,
    otherId: string
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || id === otherId) {
        return null;
    }

    const hasPast = row.cells.some(
        (value, index) => isWork(value) && value.assignmentId === otherId && columns[index] && columns[index].isPast
    );

    if (hasPast) {
        return null;
    }

    const cells = row.cells.map((value) =>
        isWork(value) && value.assignmentId === otherId ? { ...value, assignmentId: id } : value
    );

    const result = rows.slice();
    result[rowIndex] = { ...row, cells };

    return result;
}
